import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {useNavigate} from 'react-router';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import clsx from 'clsx';
import {ROUTE_PATHS} from '@di-shared/routes';
import DiagBatteryLifespan, {DiagBatteryLifespanLoading} from '@di-shared/features/diag/battery/lifespan/DiagBatteryLifespan';
import {emitEventLog} from '@di-shared/features/analytics';
import ConsentBannerLoadingKr from '@di-shared/features/home/kr/ConsentBannerLoading.kr';
import ConsentBannerKr from '@di-shared/features/home/kr/ConsentBanner.kr';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import ScrollToTopOnMount from '@di-shared/features/common/ScrollToTopOnMount';

interface DiagBatteryLifespanPageProps {
  vin: string;
}

function DiagBatteryLifespanPage({vin}: DiagBatteryLifespanPageProps) {
  const {t} = useInferableTranslation();
  const {openAlert} = useCcWebviewUtilsHelper();
  const navigate = useNavigate();
  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('T30077')}</h1>
      </Header>
      <main className="content page_car_management">
        <ErrorBoundary fallback={<></>}>
          <Suspense fallback={<ConsentBannerLoadingKr />}>
            <ConsentBannerKr vin={vin} />
          </Suspense>
        </ErrorBoundary>
        <div
          className="tabbar_txt tabbar_sticky"
          role="tablist"
        >
          <div className="inner">
            <button
              type="button"
              className={clsx('tabbar_item', 'body_large_r', {
                'is-selected': location.pathname === ROUTE_PATHS.DIAG_BATTERY_DISCHARGE.getFullPath(vin),
              })}
              onClick={() => {
                emitEventLog({
                  event: 'vehicle_status_check',
                  interaction_type: 'view 12v battery soc',
                  click_text: t('T30075'),
                  click_location: 'body',
                });
                navigate(ROUTE_PATHS.DIAG_BATTERY_DISCHARGE.getFullPath(vin), {replace: true});
              }}
              id={`${t('T30075')}_tab`}
              role="tab"
              aria-selected={location.pathname === ROUTE_PATHS.DIAG_BATTERY_DISCHARGE.getFullPath(vin)}
              aria-controls={`${t('T30075')}_tabpanel`}
              tabIndex={location.pathname === ROUTE_PATHS.DIAG_BATTERY_DISCHARGE.getFullPath(vin) ? 0 : -1}
            >
              <span className="txt">{t('T30075')}</span>
            </button>

            <button
              type="button"
              className={clsx('tabbar_item', 'body_large_r', {
                'is-selected': location.pathname === ROUTE_PATHS.DIAG_BATTERY_LIFESPAN.getFullPath(vin),
              })}
              id={`${t('T30076')}_tab`}
              role="tab"
              aria-selected={location.pathname === ROUTE_PATHS.DIAG_BATTERY_LIFESPAN.getFullPath(vin)}
              aria-controls={`${t('T30076')}_tabpanel`}
              tabIndex={location.pathname === ROUTE_PATHS.DIAG_BATTERY_LIFESPAN.getFullPath(vin) ? 0 : -1}
            >
              <span className="txt">{t('T30076')}</span>
            </button>
          </div>
        </div>
        <ErrorBoundary
          fallbackRender={() => {
            (async function () {
              await openAlert({
                title: t('T20076'),
                message: t('T30358') + '\n' + t('T10160_$tel', {tel: import.meta.env.VITE_EMERGENCY_TEL_BRAND}),
              });
              navigate(ROUTE_PATHS.HOME_VEHICLE_TAB.getFullPath(vin), {replace: true});
            })();
            return <></>;
          }}
        >
          <Suspense fallback={<DiagBatteryLifespanLoading />}>
            <DiagBatteryLifespan vin={vin} />
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
}

export default DiagBatteryLifespanPage;
